import { z } from 'zod';

export const widgetTypes = [
  'progress-snapshot',
  'investment-balance',
  'health-alerts',
  'dependency-analysis',
  'missing-data',
  'feedback-to-delivery',
  'value-effort-matrix',
] as const;
export const widgetTypeSchema = z.enum(widgetTypes);
export type WidgetType = z.infer<typeof widgetTypeSchema>;

export const progressTimeframes = ['2w', '1m', '3m', '6m', '1y', 'oldest'] as const;
export const progressTimeframeSchema = z.enum(progressTimeframes);
export type ProgressTimeframe = z.infer<typeof progressTimeframeSchema>;

export const DEFAULT_PROGRESS_TIMEFRAME: ProgressTimeframe = '3m';

const GROUP_BY_TOKENS = ['team', 'itemType', 'integration'] as const;
const MAX_GROUP_BY_TOKENS = 2;

const isValidGroupByCompact = (value: string): boolean => {
  if (value === '') return true;
  const parts = value.split('_');
  if (parts.length > MAX_GROUP_BY_TOKENS) return false;
  if (new Set(parts).size !== parts.length) return false;
  const allowed: readonly string[] = GROUP_BY_TOKENS;
  return parts.every((p) => allowed.includes(p));
};

const groupBySchema = z.string().refine(isValidGroupByCompact, {
  message:
    "Invalid groupBy. Expected an empty string, or one or two of 'team' / 'itemType' / 'integration' joined by '_' (e.g. 'team_itemType').",
});

export const DEFAULT_INVESTMENT_BALANCE_GROUP_BY = 'team';

// Per-type widget configuration. Discriminated by `type`, mirroring the connections
// table's provider_details. Widget types that need no configuration (health-alerts,
// dependency-analysis, missing-data) carry `null` on the widget row.
const progressSnapshotConfigSchema = z.object({
  type: z.literal('progress-snapshot:v1'),
  timeframe: progressTimeframeSchema,
});

const investmentBalanceConfigSchema = z.object({
  type: z.literal('investment-balance:v1'),
  groupBy: groupBySchema,
});

const valueEffortMatrixConfigSchema = z.object({
  type: z.literal('value-effort-matrix:v1'),
  xFieldId: z.number().int().positive(),
  yFieldId: z.number().int().positive(),
  sizeFieldId: z.number().int().positive(),
  colorFieldId: z.number().int().positive(),
});

export const widgetConfigSchema = z.discriminatedUnion('type', [
  progressSnapshotConfigSchema,
  investmentBalanceConfigSchema,
  valueEffortMatrixConfigSchema,
]);

export type WidgetConfig = z.infer<typeof widgetConfigSchema>;
export type ProgressSnapshotConfig = z.infer<typeof progressSnapshotConfigSchema>;
export type InvestmentBalanceConfig = z.infer<typeof investmentBalanceConfigSchema>;
export type ValueEffortMatrixConfig = z.infer<typeof valueEffortMatrixConfigSchema>;

export const DEFAULT_WIDGET_CONFIG_BY_TYPE: Partial<Record<WidgetType, WidgetConfig>> = {
  'progress-snapshot': { type: 'progress-snapshot:v1', timeframe: DEFAULT_PROGRESS_TIMEFRAME },
  'investment-balance': {
    type: 'investment-balance:v1',
    groupBy: DEFAULT_INVESTMENT_BALANCE_GROUP_BY,
  },
};

export const widgetDTOSchema = z.object({
  widgetId: z.number().int(),
  organizationId: z.string().min(1),
  loopId: z.number().int(),
  type: widgetTypeSchema,
  config: widgetConfigSchema.nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type WidgetDTO = z.infer<typeof widgetDTOSchema>;

export function parseWidgetDTO(data: unknown): WidgetDTO {
  return widgetDTOSchema.parse(data);
}

export const widgetInsertDTOSchema = z
  .object({
    loopId: z.number().int(),
    type: widgetTypeSchema,
  })
  .strict();

export type WidgetInsertDTO = z.infer<typeof widgetInsertDTOSchema>;

export const widgetConfigUpdateDTOSchema = z
  .object({
    config: widgetConfigSchema,
  })
  .strict();

export type WidgetConfigUpdateDTO = z.infer<typeof widgetConfigUpdateDTOSchema>;
